// GA4 `purchase` event.
//
// `purchase` must represent a *completed* payment. The reservation is created
// before the payment step runs, so we split this into:
//   - buildPurchasePayload(): compute the items/value/tax at create-time (when all
//     the reservation data is in hand) and return a serialisable payload.
//   - firePurchaseEvent(): actually send it (GA4 + GTM dataLayer + fbq mirror).
//   - store/firePending: stash the payload across the payment step and fire it once
//     payment is confirmed (Walley success callback / stored-card charge success).
//
// Internal flows (invoice/quota/free) are paid at create, so callers build + fire
// immediately. Walley iframe and stored-card flows defer the fire until success.

import type { PurchaseItem, PurchasePayload, PurchaseReservationDetails } from "../interfaces/PurchaseGtag";
import type { IVehicleSearch } from "../interfaces/VehicleSearch";
import { vehicleType as classifyVehicleType } from "./vehicleSegment";
import type { PricingData } from "../interfaces/PricingData";
import type { GA4Params } from "./GoogleAnalyticsEvents";
import { SERVICE_CATALOG, paymentTypeLabel } from "./ga4Ecommerce";
export type { PurchasePayload } from "../interfaces/PurchaseGtag";

type SendGa4Event = (eventName: string, params?: GA4Params) => void;

/** sessionStorage key for a purchase awaiting payment confirmation. */
export const PENDING_PURCHASE_KEY = "ga4_pending_purchase";

/**
 * Build the GA4 purchase payload from reservation data. Returns null when tracking
 * is off or required data is missing — callers then neither store nor fire.
 */
export const buildPurchasePayload = (
  gtagTracking: boolean,
  noVat: boolean,
  reservationDetails: PurchaseReservationDetails,
  vehicle: IVehicleSearch,
  reservationLength: number,
  price: number,
  reservationId: number,
  coupon?: string,
  paymentType?: string
): PurchasePayload | null => {
  if (!reservationDetails || !vehicle || !reservationLength || !reservationId || !gtagTracking) return null;
  const pricingDataType: PricingData = noVat ? vehicle.pricingDataNoVat : vehicle.pricingData;
  const items: PurchaseItem[] = [];
  let vehiclePrice = Number(price) || 0; // total - extras
  let tax = 0;
  let taxPercentageDivider = 1.255;
  let kmPrice = 0;
  const insuranceIds = String(reservationDetails?.insurance || "").split(",").filter(Boolean);

  // Labels
  const brand = vehicle?.vehicleModelData?.manufacturer ?? "";
  const model = vehicle?.vehicleModelData?.name ?? "";
  const trunk = vehicle?.vehicleModelData?.dimensions?.trunkVolume || 0;
  const itemName = `${brand} ${model}${trunk ? ` ${trunk} m3` : ""}`;

  const customerType = reservationDetails?.companyVatNumber ? "company" : "consumer";
  const rentalOrigin = reservationDetails?.oneway ? "OneWay" : "24rent";
  const vehicleType = classifyVehicleType(vehicle);
  const paymentMethod = Number(reservationDetails.paymentMethod);

  // ---- KM extra
  const km = Number(reservationDetails.km);
  if (Number.isFinite(km) && km !== 100) {
    let kmName = "";
    let kmLabel = "";
    if (km === 0) {
      kmName = "unlimitedKilometers";
      kmLabel = "Unlimited Kilometers";
      kmPrice = (pricingDataType?.coupon
        ? pricingDataType.withCoupon!.unlimitedKilometers
        : pricingDataType?.base?.unlimitedKilometers) / 100 * reservationLength;
    } else {
      kmName = `${km}km`;
      kmLabel = `${km} km`;
      kmPrice = (pricingDataType?.coupon
        ? (pricingDataType.withCoupon!.pricePerKilometer * Math.max(0, km - pricingDataType.withCoupon!.freeKilometers - 100))
        : (pricingDataType?.base?.pricePerKilometer * Math.max(0, km - 100))
      ) / 100 * reservationLength;
    }
    kmPrice = Number(kmPrice.toFixed(2));
    if (kmPrice > 0) {
      items.push({ item_id: kmName, item_name: kmLabel, price: kmPrice, quantity: 1, item_category: "mileage" });
      vehiclePrice -= kmPrice;
      tax += Number((kmPrice - (kmPrice / taxPercentageDivider)).toFixed(2));
    }
  }

  const addExtra = (id: string, raw: number, includeTax: boolean) => {
    const price = Number.isFinite(raw) ? Number(raw.toFixed(2)) : 0;
    if (price <= 0) return;
    const def = SERVICE_CATALOG[id];
    items.push({ item_id: id, item_name: def?.name ?? id, price, quantity: 1, item_category: def?.category ?? "extra" });
    vehiclePrice -= price;
    if (includeTax) {
      tax += Number((price - (price / taxPercentageDivider)).toFixed(2));
    }
  };

  if (insuranceIds.includes("1028")) {
    const v = (pricingDataType?.coupon ? pricingDataType.withCoupon!.lowDeductible : pricingDataType?.base?.lowDeductible) / 100 * reservationLength;
    addExtra("1028", v, false);
  }
  if (insuranceIds.includes("1053")) {
    const v = (pricingDataType?.coupon ? pricingDataType.withCoupon!.midDeductible : pricingDataType?.base?.midDeductible) / 100 * reservationLength;
    addExtra("1053", v, false);
  }
  if (insuranceIds.includes("1030")) {
    const v = (pricingDataType?.coupon ? pricingDataType.withCoupon!.highDeductible : pricingDataType?.base?.highDeductible) / 100 * reservationLength;
    addExtra("1030", v, false);
  }
  if (insuranceIds.includes("1027") || reservationDetails.other === 1027) {
    const v = (pricingDataType?.coupon ? pricingDataType.withCoupon!.towHitch : pricingDataType?.base?.towHitch) / 100 * reservationLength;
    addExtra("1027", v, true);
  }
  if (insuranceIds.includes("1026")) {
    const v = (pricingDataType?.coupon ? pricingDataType.withCoupon!.windshieldInsurance : pricingDataType?.base?.windshieldInsurance) / 100 * reservationLength;
    addExtra("1026", v, true);
  }
  if (insuranceIds.includes("1038")) {
    const v = (pricingDataType?.coupon ? pricingDataType.withCoupon!.tireInsurance : pricingDataType?.base?.tireInsurance) / 100 * reservationLength;
    addExtra("1038", v, true);
  }
  if (insuranceIds.includes("1040")) {
    const v = (pricingDataType?.coupon ? pricingDataType.withCoupon!.cancellationInsurance : pricingDataType?.base?.cancellationInsurance) / 100 * reservationLength;
    addExtra("1040", v, true);
  }
  if (insuranceIds.includes("1041")) {
    const v = (pricingDataType?.coupon ? pricingDataType.withCoupon!.additionalDriver : pricingDataType?.base?.additionalDriver) / 100 * reservationLength;
    addExtra("1041", v, true);
  }
  if (reservationDetails.export) {
    const v = (pricingDataType?.coupon ? pricingDataType.withCoupon!.export : pricingDataType?.base?.export) / 100 * reservationLength;
    addExtra("1023", v, true);
  }
  if (insuranceIds.includes("1047")) {
    const v = (pricingDataType?.base?.hundredTwentyFiveGramsCOTwo ?? 0) / 100 * reservationLength;
    addExtra("1047", v, true);
  }
  if (insuranceIds.includes("1048")) {
    const v = pricingDataType?.base?.fiveHundredGramsCOTwo / 100 * reservationLength;
    addExtra("1048", v, true);
  }
  if (insuranceIds.includes("1049")) {
    const v = pricingDataType?.base?.sevenHundredGramsCOTwo / 100 * reservationLength;
    addExtra("1049", v, true);
  }

  // Payment method surcharge
  if (paymentMethod === 2) {
    items.push({ item_id: "invoice", item_name: "Invoice", price: 5, quantity: 1, item_category: "fee" });
    vehiclePrice -= 5;
    tax += Number((5 - (5 / taxPercentageDivider)).toFixed(2));
  }
  if (paymentMethod === 3) {
    items.push({ item_id: "quota", item_name: "Quota", price: 0, quantity: 1, item_category: "fee" });
  }
  if (paymentMethod === 6) {
    items.push({ item_id: "free", item_name: "Free", price: 0, quantity: 1, item_category: "fee" });
  }

  // Vehicle (last)
  if (vehiclePrice < 0) {
    vehiclePrice = 0;
  } else {
    tax += Number((vehiclePrice - (vehiclePrice / taxPercentageDivider)).toFixed(2));
    vehiclePrice = Number(vehiclePrice.toFixed(2));
  }

  items.push({
    item_id: String(vehicle?.id ?? `${brand}_${model}`),
    item_name: itemName,
    item_brand: brand,
    item_variant: model,
    item_category: vehicleType,
    item_category2: customerType,
    item_category3: rentalOrigin,
    price: vehiclePrice,
    quantity: 1
  });

  tax = Number(tax.toFixed(2));

  return {
    params: {
      transaction_id: String(reservationId),
      affiliation: "24rent",
      coupon: coupon || undefined,
      value: Number(price),
      tax: tax,
      currency: "EUR",
      // Prefer the actual selected method (passed from sendPaymentGaEvent via
      // services.paymentMethod, same source as add_payment_info) so a stored-card
      // payment reports "stored_card" rather than the base "online-bank".
      payment_type: paymentType ?? paymentTypeLabel(paymentMethod),
      items
    },
    transactionId: String(reservationId),
    transactionTotal: Number(price),
  };
};

/**
 * Send the purchase event. Call this at the moment payment is actually confirmed.
 * Mirrors to the GTM `orderComplete` dataLayer event and the Facebook pixel.
 */
export const firePurchaseEvent = (payload: PurchasePayload, sendGa4Event: SendGa4Event) => {
  if (!payload) return;

  // GTM dataLayer for the orderComplete tag.
  window.dataLayer?.push({
    event: "orderComplete",
    transactionId: payload.transactionId,
    transactionTotal: payload.transactionTotal
  });

  sendGa4Event("purchase", { ...payload.params });

  // Facebook Purchase mirror.
  if (window.fbq) {
    window.fbq("track", "Purchase", {
      content_ids: [payload.transactionId],
      content_type: "product",
      value: payload.transactionTotal,
      currency: "EUR"
    });
  }
};

/** Stash a purchase payload across the payment step (Walley redirect / async charge). */
export const storePendingPurchase = (payload: PurchasePayload | null) => {
  if (!payload) return;
  try { sessionStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify(payload)); } catch { /* private mode */ }
};

/** Drop a pending purchase (e.g. on payment failure) so it can't fire later. */
export const clearPendingPurchase = () => {
  try { sessionStorage.removeItem(PENDING_PURCHASE_KEY); } catch { /* noop */ }
};

/** Read the pending purchase iff it matches the reservation being confirmed. */
const readMatchingPending = (expectedResId: string | number | undefined): PurchasePayload | null => {
  let raw: string | null = null;
  try { raw = sessionStorage.getItem(PENDING_PURCHASE_KEY); } catch { return null; }
  if (!raw) return null;
  let payload: PurchasePayload | null = null;
  try { payload = JSON.parse(raw); } catch { clearPendingPurchase(); return null; }
  if (!payload || !payload.params) return null;
  // Guard against a stale payload from an abandoned attempt attaching to a later
  // success (or to an internal/stored-card reservation that never stashed one).
  if (expectedResId != null && String(payload.transactionId) !== String(expectedResId)) return null;
  return payload;
};

/**
 * Whether a not-yet-fired purchase is waiting for this reservation. This doubles as
 * the "in-flight Walley iframe payment, first success callback" signal — it's true
 * exactly once (cleared after firePendingPurchase) and false for internal/stored-card
 * reservations (which never stash a pending purchase) and on success-URL reloads.
 */
export const hasPendingPurchase = (expectedResId: string | number | undefined): boolean =>
  readMatchingPending(expectedResId) !== null;

/**
 * Fire a previously-stored purchase once payment is confirmed, then clear it so a
 * reload of the success URL can't double-count. Returns true if it fired.
 */
export const firePendingPurchase = (
  expectedResId: string | number | undefined,
  sendGa4Event: SendGa4Event
): boolean => {
  const payload = readMatchingPending(expectedResId);
  if (!payload) return false;
  firePurchaseEvent(payload, sendGa4Event);
  clearPendingPurchase();
  return true;
};
